// BenchmarkIndicator — horizontal bar showing current value vs national benchmarks.
//
// Visual layout:
//   [current-filled-region |▼| ........... | avg | ........ | top ]
//
// The filled region is colored by performance rating (red/amber/lime/green).
// Avg and top-quartile markers are shown as vertical tick labels.

interface BenchmarkIndicatorProps {
  current: number;
  nationalAverage: number;
  topQuartile: number;
  unit: "percent" | "days" | "count";
  direction: "higher-better" | "lower-better";
  label: string;
  source?: string;
  note?: string;
  isLoading?: boolean;
}

type Rating =
  | "bottom-quartile"
  | "below-average"
  | "above-average"
  | "top-quartile";

function computeRating(
  current: number,
  nationalAverage: number,
  topQuartile: number,
  direction: "higher-better" | "lower-better",
): Rating {
  if (direction === "higher-better") {
    if (current >= topQuartile) return "top-quartile";
    if (current >= nationalAverage) return "above-average";
    if (current >= nationalAverage * 0.8) return "below-average";
    return "bottom-quartile";
  } else {
    if (current <= topQuartile) return "top-quartile";
    if (current <= nationalAverage) return "above-average";
    if (current <= nationalAverage * 1.2) return "below-average";
    return "bottom-quartile";
  }
}

const RATING_COLORS: Record<Rating, string> = {
  "top-quartile": "#16A34A",
  "above-average": "#65A30D",
  "below-average": "#D97706",
  "bottom-quartile": "#DC2626",
};

const RATING_LABELS: Record<Rating, string> = {
  "top-quartile": "Top Quartile",
  "above-average": "Above Average",
  "below-average": "Below Average",
  "bottom-quartile": "Bottom Quartile",
};

function formatVal(val: number, unit: "percent" | "days" | "count"): string {
  if (unit === "percent") return `${Math.round(val * 100)}%`;
  if (unit === "days") return `${Math.round(val)}d`;
  return String(Math.round(val));
}

/**
 * Compute the percentage position on the track (0–100).
 * For higher-better: 0 = 0, 100 = topQuartile * 1.1 (a bit past top).
 * For lower-better:  0 = 0, 100 = topQuartile (left=best), reversed so left is good.
 */
function trackPosition(val: number, min: number, max: number): number {
  const clamped = Math.max(min, Math.min(max, val));
  return ((clamped - min) / (max - min)) * 100;
}

export default function BenchmarkIndicator({
  current,
  nationalAverage,
  topQuartile,
  unit,
  direction,
  label,
  source,
  note,
  isLoading = false,
}: BenchmarkIndicatorProps) {
  if (isLoading) {
    return (
      <div className="space-y-1.5">
        <div className="skeleton h-3 w-28 rounded" />
        <div className="skeleton h-4 w-full rounded" />
      </div>
    );
  }

  const rating = computeRating(
    current,
    nationalAverage,
    topQuartile,
    direction,
  );
  const ratingColor = RATING_COLORS[rating];
  const ratingLabel = RATING_LABELS[rating];

  // Track range: 0 to (worst side) * 1.25 so there's room to show bottom-quartile
  let trackMin: number;
  let trackMax: number;

  if (direction === "higher-better") {
    // Left = 0 (bad), Right = topQuartile * 1.15 (good side)
    trackMin = 0;
    trackMax = topQuartile * 1.15;
  } else {
    // Left = topQuartile * 0.5 (good/low), Right = nationalAverage * 1.6 (bad/high)
    trackMin = topQuartile * 0.5;
    trackMax = nationalAverage * 1.6;
  }

  const currentPct = trackPosition(current, trackMin, trackMax);
  const avgPct = trackPosition(nationalAverage, trackMin, trackMax);
  const topPct = trackPosition(topQuartile, trackMin, trackMax);

  // For higher-better: fill from 0 to current; for lower-better: fill from current to max
  const fillLeft = direction === "higher-better" ? 0 : currentPct;
  const fillWidth =
    direction === "higher-better" ? currentPct : 100 - currentPct;

  return (
    <div className="space-y-1">
      {/* Label + value row */}
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-theme-secondary uppercase tracking-wide">
          {label}
        </span>
        <span
          className="text-xs font-bold tabular-nums"
          style={{ color: ratingColor }}
        >
          {formatVal(current, unit)}&ensp;
          <span className="font-normal text-theme-muted">{ratingLabel}</span>
        </span>
      </div>

      {/* Track */}
      <div
        className="relative h-3 rounded-full bg-theme-overlay overflow-visible"
        style={{ minWidth: 0 }}
      >
        {/* Filled region */}
        <div
          className="absolute top-0 h-full rounded-full transition-all"
          style={{
            left: `${fillLeft}%`,
            width: `${fillWidth}%`,
            backgroundColor: ratingColor,
            opacity: 0.35,
          }}
        />

        {/* Avg marker */}
        <div
          className="absolute top-0 h-full w-0.5 bg-theme-secondary/60"
          style={{ left: `${avgPct}%` }}
        />

        {/* Top quartile marker */}
        <div
          className="absolute top-0 h-full w-0.5"
          style={{
            left: `${topPct}%`,
            backgroundColor: "#16A34A",
            opacity: 0.7,
          }}
        />

        {/* Current marker — triangle */}
        <div
          className="absolute -top-0.5 -translate-x-1/2 w-2 h-4 flex items-center justify-center"
          style={{ left: `${currentPct}%` }}
        >
          <svg width="8" height="12" viewBox="0 0 8 12">
            <polygon points="4,0 8,12 0,12" fill={ratingColor} />
          </svg>
        </div>
      </div>

      {/* Axis labels */}
      <div className="flex justify-between text-[10px] text-theme-muted mt-0.5">
        <span>{direction === "lower-better" ? "Better" : ""}</span>
        <span>Avg {formatVal(nationalAverage, unit)}</span>
        <span style={{ color: "#16A34A" }}>
          Top {formatVal(topQuartile, unit)}
        </span>
      </div>

      {/* Note + source */}
      {(note || source) && (
        <p className="text-[10px] text-theme-muted leading-snug mt-0.5">
          {note && (
            <span className="font-medium text-theme-secondary">{note}</span>
          )}
          {note && source && " · "}
          {source && <span>{source}</span>}
        </p>
      )}
    </div>
  );
}
